import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { CATEGORIES } from "@/data/products";
import { fetchProducts } from "@/lib/api";
import { useTitle } from "@/lib/useTitle";
import ProductCard from "@/components/ProductCard";

const Products = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const activeCat = category || "all";
  const catName = activeCat === "all" ? null : CATEGORIES.find((c) => c.id === activeCat)?.name;
  useTitle(
    catName ? `${catName} — Bulk Export Supplier India | GN Agro Traders` : "Products | Indian Rice, Pulses, Wheat & Agro Commodities — GN Agro Traders",
    catName ? `Bulk ${catName} export from India by GN Agro Traders. Specifications, packaging and MOQ per buyer requirement. Request a quotation.` : "Browse GN Agro Traders' export catalogue: Basmati & Non-Basmati Rice, Pulses, Wheat, Flours and agro commodities for international B2B buyers."
  );
  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);

  useEffect(() => {
    fetchProducts().then(setAll);
  }, []);

  const filtered = useMemo(() => {
    let list = all;
    if (activeCat !== "all") list = list.filter((p) => p.category === activeCat);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => `${p.name} ${p.variety} ${p.type}`.toLowerCase().includes(s));
    }
    return list;
  }, [activeCat, q, all]);

  return (
    <div>
      <section className="bg-[#004225] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />Export Catalogue</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">Products</h1>
          <p className="mt-3 max-w-2xl text-white/80">Rice, pulses, wheat, flour and agro commodities — sourced across Indian farms, processed to buyer specification, and exported globally.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/products")} data-testid="filter-all" className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest border ${activeCat === "all" ? "bg-[#004225] text-white border-[#004225]" : "border-[#004225]/20 text-[#004225] hover:bg-[#F7F2E2]"}`}>All</button>
            {CATEGORIES.map((c) => (
              <button key={c.id} data-testid={`filter-${c.id}`} onClick={() => navigate(`/products/${c.id}`)} className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest border ${activeCat === c.id ? "bg-[#004225] text-white border-[#004225]" : "border-[#004225]/20 text-[#004225] hover:bg-[#F7F2E2]"}`}>{c.name}</button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#171717]/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search" placeholder="Search rice, pulses, wheat…" className="w-full pl-9 pr-3 py-2 rounded-full border border-[#004225]/15 bg-white text-sm outline-none focus:border-[#D4AF37]" />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
          {filtered.map((p) => (<ProductCard key={p.id} product={p} />))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16 text-[#171717]/60">No products match your search. <Link className="text-[#004225] font-semibold" to="/products">View all</Link></div>
        )}
      </section>
    </div>
  );
};

export default Products;
